import React, { useState, useContext } from "react";
import styled, { ThemeContext } from "styled-components";

// ----------------------------
// Styled Components s podrškom za temu
// ----------------------------
const TestimonialSection = styled.section`
  position: relative;
  display: block;
  padding: 120px 0;
  background-color: ${({ theme }) => theme.colors.background};
  z-index: 1;
  transition: all 0.3s ease;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  flex: 0 0 50%;
  max-width: 50%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const TestimonialLeft = styled.div`
  position: relative;
  display: block;
  margin-left: 20px;
  margin-right: 70px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 40px;
  }
`;

const TestimonialImgWrapper = styled.div`
  position: relative;
  display: block;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    display: block;
  }
`;

const TestimonialRight = styled.div`
  position: relative;
  display: block;
`;

const SectionTitle = styled.div`
  margin-bottom: 36px;
  text-align: left;
`;

const TagLine = styled.h6`
  position: relative;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.secondary};

  span {
    margin-right: 10px;
    font-size: 18px;
  }
`;

const Title = styled.h3`
  font-size: 36px;
  line-height: 46px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  transition: color 0.3s ease;
`;

const IntroText = styled.p`
  font-size: 16px;
  line-height: 26px;
  margin-bottom: 30px;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: color 0.3s ease;
`;

const TestimonialCarousel = styled.div`
  position: relative;
  display: block;
  margin-top: 30px;
`;

const TestimonialSlider = styled.div`
  position: relative;
  overflow: hidden;
`;

const TestimonialSingle = styled.div`
  position: relative;
  display: block;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 10px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  padding: 40px;
  padding-bottom: 80px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.5s ease;
  opacity: ${(props) => (props.isAnimating === "true" ? 0 : 1)};
  transform: ${(props) =>
    props.isAnimating === "true"
      ? `translateX(${
          props.direction === "right" ? "-10%" : "10%"
        }) scale(0.95)`
      : "translateX(0) scale(1)"};
`;

const Quote = styled.div`
  position: absolute;
  bottom: 50px;
  right: 40px;

  span {
    position: relative;
    display: inline-block;
    font-size: 55px;
    color: ${({ theme }) =>
      `${theme.colors.primary}40`}; /* 40 je za 25% opaciteta */
  }
`;

const TestimonialRatting = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Star = styled.span`
  position: relative;
  display: inline-block;
  font-size: 18px;
  color: ${(props) =>
    props.active === "true" ? "#f1c40f" : props.theme.colors.textSecondary + "40"};
  transition: color 0.3s ease;
`;

const StarRating = ({ rating }) => {
  const theme = useContext(ThemeContext);

  return (
    <TestimonialRatting>
      {[...Array(5)].map((_, index) => (
        <Star key={index} active={(index < rating).toString()} theme={theme}>
          ★
        </Star>
      ))}
    </TestimonialRatting>
  );
};

const TestimonialText = styled.p`
  font-size: 22px;
  line-height: 32px;
  margin: 25px 0;
  color: ${({ theme }) => theme.colors.text};
  transition: color 0.3s ease;
`;

const ClientInfo = styled.div`
  position: relative;
  display: block;

  h4 {
    font-size: 20px;
    font-weight: 700;
    line-height: 30px;
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    transition: color 0.3s ease;
  }

  p {
    margin-top: 5px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSecondary};
    transition: color 0.3s ease;
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.textSecondary + "40"};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 18px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
`;

const Dot = styled.button`
  width: ${(props) => (props.active ? "20px" : "8px")};
  height: 8px;
  border-radius: 4px;
  background-color: ${(props) =>
    props.active
      ? props.theme.colors.primary
      : props.theme.colors.textSecondary + "40"};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
  }
`;

// ----------------------------
// Testimonial podaci
// ----------------------------
const testimonials = [
  {
    id: 1,
    rating: 5,
    text: "Učestvovanje na prošlogodišnjem hakatonu kao takmičarka bilo je jedno jako korisno i zanimljivo iskustvo – upoznala sam sjajne ljude, stekla dodatnu motivaciju za dalji rad na sebi i stvorila uspomene koje ću zauvijek nositi sa sobom.",
    name: "Amina Grbović",
    title: "Takmičarka",
  },
  {
    id: 2,
    rating: 5,
    text: "Bilo je to izuzetno iskustvo koje mi je omogućilo da unapredim svoje tehničke veštine i radim u dinamičnom timskom okruženju.",
    name: "Bakir Demić",
    title: "Takmičar",
  },
  {
    id: 3,
    rating: 4,
    text: "Nevjerovatan doživljaj! Hakaton je bio prilika da se povežem sa talentovanim ljudima i radim na izazovnim projektima. Organizacija je bila odlična, a atmosfera inspirativna.",
    name: "Umejr Zukorlić",
    title: "Takmičar",
  },
];

// ----------------------------
// Komponenta
// ----------------------------
function TestimonialOne() {
  const theme = useContext(ThemeContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState("right");
  const [isAnimating, setIsAnimating] = useState(false);

  const goToPrevious = () => {
    if (isAnimating) return;

    setDirection("left");
    setIsAnimating(true);

    setTimeout(() => {
      const isFirstSlide = currentIndex === 0;
      const newIndex = isFirstSlide
        ? testimonials.length - 1
        : currentIndex - 1;
      setCurrentIndex(newIndex);
      setIsAnimating(false);
    }, 300);
  };

  const goToNext = () => {
    if (isAnimating) return;

    setDirection("right");
    setIsAnimating(true);

    setTimeout(() => {
      const isLastSlide = currentIndex === testimonials.length - 1;
      const newIndex = isLastSlide ? 0 : currentIndex + 1;
      setCurrentIndex(newIndex);
      setIsAnimating(false);
    }, 300);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentIndex) return;

    setDirection(index > currentIndex ? "right" : "left");
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, 300);
  };

  return (
    <TestimonialSection>
      <Container>
        <Row>
          <Column>
            <TestimonialRight>
              <SectionTitle>
                <TagLine>
                  <span>✦</span>
                  Utisci učesnika
                </TagLine>
                <Title>Šta kažu naši zadovoljni učesnici</Title>
              </SectionTitle>
              <IntroText>
              Učešće na hakatonu donosi neprocenjiva znanja, nova poznanstva i 
              priliku da se radom pod pritiskom razviju važne veštine. 
              Pogledajte šta naši takmičari 
              kažu o svojim iskustvima i kako im je hakaton pomogao da rastu i napreduju.
              </IntroText>
              <TestimonialCarousel>
                <TestimonialSlider>
                  <TestimonialSingle
                    isAnimating={isAnimating.toString()}
                    direction={direction}>
                    <Quote>
                      <span>❝</span>
                    </Quote>
                    <StarRating rating={testimonials[currentIndex].rating} />
                    <TestimonialText>
                      {testimonials[currentIndex].text}
                    </TestimonialText>
                    <ClientInfo>
                      <h4>{testimonials[currentIndex].name}</h4>
                      <p>{testimonials[currentIndex].title}</p>
                    </ClientInfo>
                  </TestimonialSingle>
                </TestimonialSlider>

                <Navigation>
                  <NavButton
                    onClick={goToPrevious}
                    disabled={isAnimating}
                    aria-label="Prethodni testimonial">
                    ←
                  </NavButton>
                  <NavButton
                    onClick={goToNext}
                    disabled={isAnimating}
                    aria-label="Sledeći testimonial">
                    →
                  </NavButton>
                </Navigation>

                <DotsContainer>
                  {testimonials.map((_, index) => (
                    <Dot
                      key={index}
                      active={index === currentIndex}
                      onClick={() => goToSlide(index)}
                      aria-label={`Pređi na testimonial ${index + 1}`}
                    />
                  ))}
                </DotsContainer>
              </TestimonialCarousel>
            </TestimonialRight>
          </Column>
        </Row>
      </Container>
    </TestimonialSection>
  );
}

export default TestimonialOne;
